import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";
import {
  ArrowRight,
  CheckCircle,
  Target,
  Cog,
  LineChart,
  Users,
  Globe,
  Megaphone,
  Workflow,
  Bot,
  Layers,
  RefreshCw,
} from "lucide-react";

const GrowthSystemsPage = () => {
  const systems = [
    {
      icon: Target,
      title: "Strategic Growth System",
      description: "A systematic approach to planning and executing business growth with clear roadmaps and measurable milestones.",
      components: ["Growth audit", "Opportunity mapping", "Roadmap development", "Milestone tracking"],
      link: "/services/business-growth-strategy",
    },
    {
      icon: Cog,
      title: "Marketing Automation System",
      description: "Automated marketing infrastructure that nurtures leads and drives conversions without manual intervention.",
      components: ["Email sequences", "Lead scoring", "Behavioral triggers", "Multi-channel automation"],
      link: "/services/marketing-automation",
    },
    {
      icon: LineChart,
      title: "Sales Pipeline System",
      description: "Structured sales process with clear stages, automation, and visibility into every opportunity.",
      components: ["Pipeline stages", "Deal automation", "Activity tracking", "Revenue forecasting"],
      link: "/services/sales-funnels-crm",
    },
    {
      icon: Users,
      title: "Lead Generation System",
      description: "Systematic approach to attracting and qualifying prospects through multiple channels.",
      components: ["Lead magnets", "Landing pages", "Qualification flows", "Lead routing"],
      link: "/services/lead-generation",
    },
    {
      icon: Globe,
      title: "Organic Growth System",
      description: "SEO and content infrastructure that builds long-term, sustainable organic traffic.",
      components: ["Content strategy", "SEO optimization", "Authority building", "Traffic analytics"],
      link: "/services/seo-content",
    },
    {
      icon: Megaphone,
      title: "Paid Acquisition System",
      description: "Scalable advertising infrastructure across Meta and Google with optimization automation.",
      components: ["Campaign structure", "Audience systems", "Creative testing", "ROAS optimization"],
      link: "/services/paid-media",
    },
  ];

  return (
    <div className="pt-32" data-testid="growth-systems-page">
      <SEO 
        title="Growth Systems - SEO & Automation Framework"
        description="Build scalable SEO, marketing automation, and lead generation systems. Our growth systems deliver compounding results through strategic automation and AI-powered optimization."
        keywords="growth systems, scalable marketing automation, SEO systems, lead generation system, marketing infrastructure, business automation framework"
      />
      <Breadcrumb items={[{ name: "Growth Systems" }]} />
      {/* Hero Section */}
      <section className="section" data-testid="growth-systems-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="badge badge-blue mb-4">Growth Systems</span>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold text-optima-dark mb-6">
                Scalable SEO & Marketing Automation Systems
              </h1>
              <p className="text-lg text-slate-600 mb-8">
                Tactics come and go, but systems endure. We build growth infrastructure that delivers consistent, predictable results—month after month.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="growth-systems-cta"
              >
                Build Your Growth System <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-alt" data-testid="philosophy-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-cyan mb-4">Our Philosophy</span>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">
                Why Systems Beat Tactics
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Most businesses chase the latest marketing tactic—the new social platform, the trending ad format, the growth hack of the week. The problem? Tactics have diminishing returns. What works today won't work tomorrow.
              </p>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Systems are different. A well-designed growth system adapts, improves, and compounds over time. It's not dependent on any single tactic or channel. It's infrastructure that supports sustainable growth.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 shadow-soft">
                  <RefreshCw className="w-8 h-8 text-optima-blue mb-2" />
                  <h4 className="font-semibold text-optima-dark">Compounding</h4>
                  <p className="text-sm text-slate-600">Results improve over time</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-soft">
                  <Layers className="w-8 h-8 text-optima-blue mb-2" />
                  <h4 className="font-semibold text-optima-dark">Scalable</h4>
                  <p className="text-sm text-slate-600">Grows without breaking</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600"
                alt="Scalable growth systems for SEO and marketing automation infrastructure"
                className="rounded-2xl shadow-strong w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Systems Grid */}
      <section className="section" data-testid="systems-grid">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="badge badge-blue mb-4">Our Systems</span>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-optima-dark mb-6">
              Growth Systems We Build
            </h2>
            <p className="text-slate-600 text-lg">
              Each system is designed to address a specific growth challenge, with components that work together seamlessly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {systems.map((system, index) => (
              <motion.div
                key={system.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={system.link}
                  className="card service-card block p-6 h-full"
                  data-testid={`system-card-${index}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-optima-blue to-optima-cyan flex items-center justify-center mb-4">
                    <system.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-optima-dark mb-2">
                    {system.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">{system.description}</p>
                  <div className="space-y-2">
                    {system.components.map((component) => (
                      <div key={component} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-optima-cyan flex-shrink-0" />
                        <span className="text-sm text-slate-600">{component}</span>
                      </div>
                    ))}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="section-alt" data-testid="integration-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="badge badge-pink mb-4">Integrated Approach</span>
              <h2 className="font-heading text-3xl font-bold text-optima-dark mb-6">
                Systems That Work Together
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                The real power comes from integration. Each system we build is designed to connect with others, creating a unified growth engine for your business.
              </p>
              <div className="bg-white rounded-2xl p-8 shadow-soft">
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="px-4 py-2 bg-optima-blue/10 rounded-full text-optima-blue font-medium">Lead Gen</div>
                  <span className="text-slate-400">→</span>
                  <div className="px-4 py-2 bg-optima-cyan/10 rounded-full text-optima-cyan font-medium">CRM</div>
                  <span className="text-slate-400">→</span>
                  <div className="px-4 py-2 bg-optima-pink/10 rounded-full text-optima-pink font-medium">Nurture</div>
                  <span className="text-slate-400">→</span>
                  <div className="px-4 py-2 bg-green-100 rounded-full text-green-600 font-medium">Convert</div>
                  <span className="text-slate-400">→</span>
                  <div className="px-4 py-2 bg-purple-100 rounded-full text-purple-600 font-medium">Retain</div>
                </div>
                <p className="text-slate-500 mt-6 text-sm">
                  A continuous cycle powered by automation and optimized by data.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-optima-dark" data-testid="growth-systems-final-cta">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Build Your Growth System?
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Book a free growth audit and we'll map out the systems your business needs to scale.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-optima-blue rounded-full font-semibold hover:bg-slate-50 transition-colors"
              data-testid="growth-systems-book-call"
            >
              Book a Free Growth Audit <ArrowRight className="w-5 h-5" />
            </Link>
            <div className="flex flex-wrap justify-center gap-4 mt-6">
              <Link to="/services" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Explore Services</Link>
              <Link to="/pricing" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">View Pricing</Link>
              <Link to="/case-studies" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Case Studies</Link>
              <Link to="/software" className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors">Our Software</Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GrowthSystemsPage;
